import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS } from '@troia-v3/types';

export interface AudienceImportEventPayload {
  audienceId: string;
  jobId?: string;
  totalLines?: number;
  imported?: number;
  duplicatesIgnored?: number;
  invalidRejected?: number;
  errorMessage?: string;
  progress?: number;
}

export interface AudienceImportSocketHandlers {
  onStarted?: (data: AudienceImportEventPayload) => void;
  onProgress?: (data: AudienceImportEventPayload) => void;
  onCompleted?: (data: AudienceImportEventPayload) => void;
  onFailed?: (data: AudienceImportEventPayload) => void;
}

/**
 * Subscribe to audience import socket events for a specific audience.
 * Filters events by audienceId and invalidates relevant React Query caches.
 */
export const useAudienceImportSocket = (
  audienceId: string | undefined,
  handlers: AudienceImportSocketHandlers = {}
): void => {
  const { socket, isConnected } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !isConnected || !audienceId) return;

    const matchAudience = (data: AudienceImportEventPayload): boolean => {
      return data && data.audienceId === audienceId;
    };

    const handleStarted = (data: AudienceImportEventPayload) => {
      if (!matchAudience(data)) return;
      queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
      handlers.onStarted?.(data);
    };

    const handleProgress = (data: AudienceImportEventPayload) => {
      if (!matchAudience(data)) return;
      handlers.onProgress?.(data);
    };

    const handleCompleted = (data: AudienceImportEventPayload) => {
      if (!matchAudience(data)) return;
      queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audience-entries', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
      handlers.onCompleted?.(data);
    };

    const handleFailed = (data: AudienceImportEventPayload) => {
      if (!matchAudience(data)) return;
      queryClient.invalidateQueries({ queryKey: ['audience', audienceId] });
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
      handlers.onFailed?.(data);
    };

    socket.on(SOCKET_EVENTS.AUDIENCE_IMPORT_STARTED, handleStarted);
    socket.on(SOCKET_EVENTS.AUDIENCE_IMPORT_PROGRESS, handleProgress);
    socket.on(SOCKET_EVENTS.AUDIENCE_IMPORT_COMPLETED, handleCompleted);
    socket.on(SOCKET_EVENTS.AUDIENCE_IMPORT_FAILED, handleFailed);

    return () => {
      socket.off(SOCKET_EVENTS.AUDIENCE_IMPORT_STARTED, handleStarted);
      socket.off(SOCKET_EVENTS.AUDIENCE_IMPORT_PROGRESS, handleProgress);
      socket.off(SOCKET_EVENTS.AUDIENCE_IMPORT_COMPLETED, handleCompleted);
      socket.off(SOCKET_EVENTS.AUDIENCE_IMPORT_FAILED, handleFailed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, audienceId]);
};
